import React from "react";

// Import images (Ensure paths are correct)
import microalgae1 from "../assets/microalgae1.webp";
import microalgae2 from "../assets/microalgae2.webp";
import microalgae3 from "../assets/microalgae3.webp";
import microalgae4 from "../assets/microalgae4.webp";
import microalgae5 from "../assets/microalgae5.webp";

const SectionWithImages = ({ id, title, text, images = [], reverse = false, cultivation, products, application }) => {
    return (
        <div 
            id={id} 
            className={`flex flex-col sm:flex-row ${reverse ? "sm:flex-row-reverse" : ""} items-center gap-8 bg-gray-900 text-white p-10 sm:p-16 lg:p-20 transition-all duration-300`}
        >
            {/* Text Section */}
            <div className="w-full sm:w-1/2 text-left animate-fade-in">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">{title}</h2>
                <p className="text-lg sm:text-xl mb-6 leading-relaxed text-justify">{text}</p>
                {cultivation && <p className="text-lg font-semibold mb-2 text-justify">Cultivation: <span className="font-normal">{cultivation}</span></p>}
                {products && <p className="text-lg font-semibold mb-2 text-justify">Products: <span className="font-normal">{products}</span></p>}
                {application && <p className="text-lg font-semibold text-justify">Application: <span className="font-normal">{application}</span></p>}
            </div>

            {/* Image Section */}
            {images.length > 0 && (
                <div className="w-full sm:w-1/2 flex justify-center animate-fade-in">
                    {images.map((image, index) => (
                        <img
                            key={index}
                            src={image}
                            alt={`${title} ${index + 1}`}
                            className="rounded-lg shadow-lg transform hover:scale-105 transition duration-300 w-3/5 max-w-[400px] max-h-[250px] object-contain"
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const Microalgae = () => {
    return (
        <div className="w-full">
            {/* Introduction Section */}
<div id="microalgae" className="bg-gray-900 text-white p-12 sm:p-20 text-center">
    <h2 className="text-4xl sm:text-5xl font-bold mb-6">Microalgae</h2>
    <p className="text-lg sm:text-xl leading-relaxed text-justify">
        Due to their expanding industrial cultivation potential, microalgae are important participants in various markets. Our company provides pure and safe microalgae products for nutraceuticals, animal feed, agriculture, and cosmetic sectors. We aim to become a global leader in food safety, innovation, and high-quality microalgae-derived products. Our production plan includes cultivating the following microalgae strains:
    </p>
</div>


            {/* Alternating Sections */}
            <SectionWithImages
                id="spirulina"
                title="Arthrospira platensis (Spirulina)"
                text="Spirulina is a filamentous, multicellular cyanobacterium with a spiral shape. Its blue-green color comes from phycocyanin and chlorophyll-a. Spirulina has been consumed for generations due to its well-balanced nutritional profile, rich in iron, vitamin B12, phycocyanin, omega-3 fatty acids, and complete protein. We aim to produce organic, artisanal spirulina in powder, tablet, or paste form."
                images={[microalgae1]}
                cultivation="Open raceway ponds or tubular photobioreactors"
                products="Slurry, powder, tablets"
                application="Food Industry, Aquafeed, Cosmetics & Beauty Care, Agriculture"
                reverse={true}
            />
            <SectionWithImages
                id="chlorella"
                title="Chlorella vulgaris"
                text="Chlorella is a single-celled green algae found in freshwater and is among the most widely cultivated algae globally. It is protein-rich, containing all essential amino acids, high in chlorophylls, and has a lipid profile with 60% polyunsaturated fatty acids. It is also rich in vitamins and minerals such as vitamin B12, iron, potassium, phosphorus, and calcium. It is presented as a fine chlorophyll-green powder."
                images={[microalgae2]}
                cultivation="Open raceway ponds or tubular photobioreactors"
                products="Slurry, powder, tablets"
                application="Dietary Supplements, Food Ingredients, Cosmetics & Beauty Care, Agriculture"
                reverse={false}
            />
            <SectionWithImages
                id="dunaliella"
                title="Tetraselmis / Dunaliella salina"
                text="Dunaliella salina thrives in saltwater environments and is rich in carotenoids, particularly beta-carotene, making it valuable for commercial applications such as food coloring, nutritional supplements, and cosmetics. Tetraselmis chui contains 45% protein, high levels of chlorophylls, and a lipid profile rich in omega-3 fatty acids (25% ALA and 8% EPA)."
                images={[microalgae3]}
                cultivation="Laboratory scale / Tubular photobioreactors"
                products="Carotenoids (β-carotene), lipids, and glycerol"
                application="Dietary Supplements, Food Ingredients, Health, Food Industry"
                reverse={true}
            />
            <SectionWithImages
                id="haematococcus"
                title="Haematococcus pluvialis"
                text="This freshwater green microalgae is known for producing astaxanthin, a powerful antioxidant with anti-inflammatory, anticancer, neuroprotective, cardiovascular, immune-enhancing, and skin-protective properties."
                images={[microalgae4]}
                cultivation="Open raceway ponds or tubular photobioreactors"
                products="Powder and Capsule"
                application="Natural Colorant, Antioxidation, Anticancer Effect, Immunity Booster, Retinal Function Improvement, Skin Protection"
                reverse={false}
            />
            <SectionWithImages
                id="chlamydomonas"
                title="Chlamydomonas reinhardtii"
                text="A single-cell green algae with biflagellate structure, widely found in soil and freshwater. It has a unique cell wall, a large cup-shaped chloroplast, a pyrenoid, and an 'eyespot' for sensing light. Commercially, it is valuable for biopharmaceuticals, biofuel production, and research in hydrogen energy."
                images={[microalgae5]}
                cultivation="Laboratory scale / Tubular photobioreactors"
                products="Algal extracts and Biomass"
                application="Biostimulants, Wastewater Treatment, Bioenergy, Biofuel"
                reverse={true}
            />
        </div>
    );
};

export default Microalgae;
